import React, { useState, useEffect } from "react";
import FetchFundraisersComponents from "../../components/Warehouse/FetchFundraisers";
import ShippingTypeDisplayComponent from "../../components/Warehouse/ShippingDisplay";
import "./WarehouseOrder.css";
import {
  SortOrder,
  TimePeriod,
  WarehouseItems,
} from "../../Interfaces/Warehouse";
import ItemsNeeded from "../../components/Warehouse/ItemsNeeded";
import { useParams } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import AddBrochureOrder from "../../components/Warehouse/AddBrochureOrder";
import FileGenerationButtons from "../../components/Warehouse/FileGenerationButtons";
import config from "../../config";
import fetchWithAuth from "../../services/fetchWithAuth";
import FundraiserPayoutTable from "../../components/Warehouse/FundraiserPayoutTable";
import { LoggedInAccount } from "../../Interfaces/AccountUsers";
import { getCurrentlyLoggedInUser } from "../../CommonFunctions";

// TODO : Limit button presses while loading data
const WarehouseOrderPage: React.FC = () => {
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriod>(
    TimePeriod.All,
  );
  const [showPopup, setShowPopup] = useState(false);
  const [selectedSortOrder, setSelectedSortOrder] = useState<SortOrder>(
    SortOrder.NAME_ALPHABETICAL,
  );
  const [loggedInAccount, setLoggedInAccount] =
    useState<LoggedInAccount | null>(null);
  const [warehouseReferenceItems, setWarehouseReferenceItems] =
    useState<WarehouseItems>({
      needed_skus: [],
      fundraiser_payout: {
        total_collected: 0,
        mamfam_profit: 0,
        total_amount_owed: 0,
        total_paid_so_far: 0,
        total_paid_remaining: 0,
      },
    });

  const handleTimePeriodChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedTimePeriod(event.target.value as TimePeriod);
  };

  const handleSortOrderChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedSortOrder(event.target.value as SortOrder);
  };

  useEffect(() => {
    const fetchLoggedInAccount = async () => {
      const account = await getCurrentlyLoggedInUser();
      setLoggedInAccount(account);
    };
    fetchLoggedInAccount();

    document.title = "MamFam Warehouse";
  }, []);
  const { fundraiserID } = useParams<{ fundraiserID: string }>();
  const openPopup = () => setShowPopup(true);
  const closePopup = () => setShowPopup(false);

  const navigate = useNavigate();

  // Convert to number or set to 0 if not present
  const id = fundraiserID ? Number(fundraiserID) : 0;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fundraiserName = queryParams.get("fundraiserName") || "All"; // Default to "All" if not present
  const brochureAccount: number =
    Number(queryParams.get("brochureOnlyAccount")) || -1;

  useEffect(() => {
    const fetchWarehouseItems = async () => {
      let url = `${config.API_BASE_URL}orders/warehouse/`;

      if (fundraiserID) {
        url = url + `?fundraiser=${fundraiserID}`;
      }

      try {
        const response = await fetchWithAuth(`${url}`);
        if (!response.ok) {
          throw new Error("Failed to fetch warehouse items");
        }
        const data: WarehouseItems = await response.json();
        setWarehouseReferenceItems(data);
      } catch (error) {
        console.error("Error fetching warehouse items:", error);
      }
    };

    fetchWarehouseItems();
  }, [fundraiserID]);

  const navigationButtons = [
    {
      label: "View All Pending Orders",
      link: "/warehouse/orders/",
    },
    {
      label: "View All Orders",
      link: "/warehouse/orders/all",
    },
    {
      label: "View Current Inventory",
      link: "/warehouse/inventory-stock",
    },
    {
      label: "View Inventory Changes",
      link: "/warehouse/inventory-changes",
    },
    {
      label: "View Inventory Remaining",
      link: "/warehouse/inventory-remaining",
    },
  ];

  return (
    <div className="warehouse-order-page-container">
      {navigationButtons.map((button: { label: string; link: string }) => (
        <button onClick={() => navigate(button.link)}>{button.label}</button>
      ))}
      <h2 className="warehouse-page-fundraiser-header-display">
        {fundraiserName} Pending Orders
      </h2>

      {id !== 0 &&
        (brochureAccount !== -1 ? (
          <button onClick={openPopup}>Enter Brochure Orders</button>
        ) : (
          <p>This fundraiser has brochures disabled.</p>
        ))}

      <div className="warehouse-order-page-shipping">
        <div className="warehouse-order-page-item">
          <ShippingTypeDisplayComponent
            header="Ship to Home"
            order_type="home"
            fundraiser_id={id}
            loggedInAccount={loggedInAccount}
          />
        </div>
        <div className="warehouse-order-page-item">
          <ShippingTypeDisplayComponent
            header="Bulk Shipping"
            order_type="bulk"
            fundraiser_id={id}
            loggedInAccount={loggedInAccount}
          />
        </div>
      </div>
      {id !== 0 && (
        <FileGenerationButtons
          fundraiserID={id}
          fundraiserName={fundraiserName}
        />
      )}
      {id !== 0 && (
        <FundraiserPayoutTable
          payout={warehouseReferenceItems.fundraiser_payout}
        />
      )}

      <div className="warehouse-order-page-fundraiser-list">
        {id === 0 && (
          <>
            <label htmlFor="timePeriodSelect">Select Time Period:</label>
            <select
              id="timePeriodSelect"
              value={selectedTimePeriod}
              onChange={handleTimePeriodChange}
            >
              {Object.entries(TimePeriod).map(([label, value]) => (
                <option key={value} value={value}>
                  {label.replaceAll("_", " ")}{" "}
                  {/* This will convert 'THREE_DAYS' to 'THREE DAYS' */}
                </option>
              ))}
            </select>

            <label htmlFor="sortOrderSelect">Select Sort Order:</label>
            <select
              id="sortOrderSelect"
              value={selectedSortOrder}
              onChange={handleSortOrderChange}
            >
              {Object.entries(SortOrder).map(([label, value]) => (
                <option key={value} value={value}>
                  {label.replaceAll("_", " ")}{" "}
                  {/* This will convert 'THREE_DAYS' to 'THREE DAYS' */}
                </option>
              ))}
            </select>

            {/* Pass the selected time period to FetchFundraisersComponents */}
            <FetchFundraisersComponents
              timePeriod={selectedTimePeriod}
              sortOrder={selectedSortOrder}
            />
          </>
        )}
      </div>
      <div className="warehouse-order-page-items-needed-table">
        {warehouseReferenceItems.needed_skus && (
          <ItemsNeeded itemsNeeded={warehouseReferenceItems.needed_skus} />
        )}
      </div>
      {showPopup && (
        <AddBrochureOrder
          fundraiserID={id}
          onClose={closePopup}
          brochureOnlyAccountID={brochureAccount}
        />
      )}
    </div>
  );
};

export default WarehouseOrderPage;
